import {
    Children,
    Fragment,
    useCallback,
    useState,
} from 'react';
import {
    Box,
    Heading,
    Stack,
    Button,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Accordion,
    Flex,
    Spacer,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import { Card } from 'components/v4/Card';
import {
    breadCrumbStyling,
    headingStyling,
    subHeadingStyling,
    accordionHeadingStyling,
} from './formStyles';
import { customTabStyles, selectedTabStyles } from '../../../constants';
import { useNavigate, Link } from 'react-router-dom';
import useAutoBackOnManagingChange from 'hooks/useAutoBackOnManagingChange';

const FormAccordionBase = ({
    children,
    breadCrumbTrail,
    header,
    submitButtonText,
    initialValues,
    finalFormSubmit,
    subHeader,
    subHeaderComponent,
    noBackBtn,
    ufBackBtn,
    ufSaveBtn,
    backLink,
    extraCards,
    extraButton,
    middleButton,
    sideDrawer,
    defaultIndex,
    controller,
    isSubmitDisabled,
    isLoading = false,
    enableReinitialize = true,
    // Tab-related props
    enableTabs = false,
    defaultTabIndex = 0,
    autoBackOnManagingChange = true,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const navigate = useNavigate();
    const [tabIndex, setTabIndex] = useState(defaultTabIndex);

    const navBack = useCallback(() => {
        if (!backLink) {
            return;
        }

        if (typeof backLink === 'function') {
            backLink();
        } else if (Array.isArray(backLink)) {
            navigate(backLink[0], backLink[1]);
        } else {
            navigate(backLink);
        }
    }, [backLink, navigate]);

    useAutoBackOnManagingChange({
        enabled: autoBackOnManagingChange && Boolean(backLink),
        onBack: navBack,
    });

    const handleAccordion = (
        errors,
        touched,
        values,
        setValues,
        tabContent = null,
    ) => {
        const accordionChildren = tabContent
            ? tabContent.accordions || []
            : typeof children === 'function'
              ? children({ errors, touched, values, setValues })
              : children;
        const getDefaultIndex = () => {
            if (tabContent && accordionChildren) {
                const childrenArray = Array.isArray(accordionChildren)
                    ? accordionChildren
                    : [accordionChildren];
                return childrenArray.map((_, index) => index);
            }
            return defaultIndex !== -1 ? [defaultIndex] : [];
        };

        return (
            <Accordion
                allowToggle
                defaultIndex={getDefaultIndex()}
                index={controller ? controller.openIndexes : undefined}
                onChange={controller ? controller.setOpenIndexes : undefined}
                allowMultiple
                sx={{ margin: '0 !important' }}>
                {Children?.map(accordionChildren, (child) => child)}
            </Accordion>
        );
    };

    const renderTabContent = (formProps, tabContent) => {
        return (
            <Card
                borderRadius={15}
                padding="30px"
                maxWidth={895}
                ml={0}
                bg={darkMode ? 'dark.tcap' : null}>
                <Stack sx={{ margin: '0 !important' }}>
                    {tabContent.subHeader && (
                        <Heading sx={subHeadingStyling}>
                            {tabContent.subHeader}
                        </Heading>
                    )}
                    {handleAccordion(
                        formProps.errors,
                        formProps.touched,
                        formProps.values,
                        formProps.setValues,
                        tabContent,
                    )}
                </Stack>
            </Card>
        );
    };

    const renderContent = (formProps) => {
        // Get tabs from children function when enableTabs is true
        if (enableTabs && typeof children === 'function') {
            const tabs = children({
                errors: formProps.errors,
                touched: formProps.touched,
                values: formProps.values,
                setValues: formProps.setValues,
            });

            // Validate that tabs is an array
            if (!Array.isArray(tabs)) {
                console.error(
                    'When enableTabs is true, children function must return an array of tab objects',
                );
                return null;
            }

            return (
                <>
                    {subHeader && (
                        <Flex
                            sx={{
                                paddingBottom: '15px',
                                paddingTop: '25px',
                            }}>
                            <Heading sx={subHeadingStyling}>
                                {subHeader}
                            </Heading>
                            <Spacer />
                            <Box paddingRight="34px">{subHeaderComponent}</Box>
                        </Flex>
                    )}
                    <Tabs index={tabIndex} onChange={setTabIndex} isLazy>
                        <TabList borderBottom={0} mb="10px">
                            {tabs.map((tab, index) => (
                                <Tab
                                    key={index}
                                    sx={customTabStyles}
                                    _selected={selectedTabStyles}>
                                    {tab.label}
                                </Tab>
                            ))}
                        </TabList>
                        <TabPanels>
                            {tabs.map((tab, index) => (
                                <TabPanel key={index} px={0} py={0}>
                                    {renderTabContent(formProps, tab)}
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </Tabs>
                </>
            );
        } else {
            // No tabs - render single accordion card
            return (
                <Card
                    borderRadius={15}
                    padding="30px 30px"
                    maxWidth={895}
                    ml={0}
                    bg={darkMode ? 'dark.tcap' : null}>
                    <Stack sx={{ margin: '0 !important' }}>
                        {subHeader && (
                            <Flex
                                sx={{
                                    paddingBottom: subHeaderComponent
                                        ? '15px'
                                        : '0px',
                                }}>
                                <Heading sx={subHeadingStyling}>
                                    {subHeader}
                                </Heading>
                                {subHeaderComponent && (
                                    <>
                                        <Spacer />
                                        <Box paddingRight="34px">
                                            {subHeaderComponent}
                                        </Box>
                                    </>
                                )}
                            </Flex>
                        )}
                        {handleAccordion(
                            formProps.errors,
                            formProps.touched,
                            formProps.values,
                            formProps.setValues,
                        )}
                    </Stack>
                </Card>
            );
        }
    };

    return (
        <Box>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        {Array.isArray(crumb.path) ? (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path[0]}
                                state={crumb.path[1]}>
                                {crumb.label}
                            </BreadcrumbLink>
                        ) : (
                            <BreadcrumbLink
                                as={Link}
                                to={
                                    typeof crumb.path === 'function'
                                        ? '#'
                                        : crumb.path
                                }
                                onClick={() =>
                                    typeof crumb.path === 'function'
                                        ? crumb.path()
                                        : null
                                }>
                                {crumb.label}
                            </BreadcrumbLink>
                        )}
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <Stack>
                {typeof header === 'string' ? (
                    <Heading sx={{ ...headingStyling, fontSize: '20px' }}>
                        {header}
                    </Heading>
                ) : (
                    header
                )}
            </Stack>
            <Formik
                initialValues={{ formValues: initialValues }}
                enableReinitialize={enableReinitialize}
                onSubmit={(value) => finalFormSubmit(value)}
                validateOnChange={true}
                validateOnBlur={true}
                validateOnMount={true}>
                {(formProps) => {
                    const { handleSubmit, isSubmitting } = formProps;
                    const submitting = isLoading || isSubmitting;
                    const submitText = submitButtonText || 'Save';
                    const loadingText = submitText.endsWith('e') && !submitText.endsWith('ee')
                        ? submitText.slice(0, -1) + 'ing'
                        : submitText + 'ing';
                    return (
                        <form onSubmit={handleSubmit}>
                            <Stack spacing={30}>
                                {renderContent(formProps)}
                                {!!extraCards &&
                                    extraCards.map((item, index) => (
                                        <Fragment key={index}>
                                            <Heading
                                                sx={{
                                                    ...accordionHeadingStyling,
                                                    marginTop: 30,
                                                }}
                                                className={
                                                    darkMode
                                                        ? 'text-white'
                                                        : 'text-black'
                                                }>
                                                {item?.props?.heading}
                                            </Heading>
                                            <Card
                                                key={index}
                                                borderRadius={15}
                                                maxWidth={895}
                                                padding="30px 30px"
                                                ml={0}
                                                bg={
                                                    darkMode
                                                        ? 'dark.tcap'
                                                        : null
                                                }>
                                                {item}
                                            </Card>
                                        </Fragment>
                                    ))}
                                <Stack
                                    direction="row"
                                    sx={{
                                        paddingBottom: sideDrawer ? 3 : 10,
                                        marginLeft: sideDrawer && 0,
                                        marginTop: sideDrawer && 25,
                                        paddingTop: sideDrawer && 3,
                                        position: sideDrawer
                                            ? 'sticky'
                                            : 'static',
                                        left: sideDrawer ? 0 : 'auto',
                                        bottom: sideDrawer ? -35 : 'auto',
                                        backgroundColor:
                                            sideDrawer &&
                                            (darkMode
                                                ? 'var(--chakra-colors-dark-bgDark)'
                                                : '#F5F5F5'),
                                        zIndex: 1,
                                        width: '100%',
                                        justifyContent: 'space-between',
                                    }}>
                                    <div
                                        className="edit-form-buttons"
                                        style={{
                                            border: 'none',
                                            textAlign: 'left',
                                            justifyContent: 'flex-start',
                                            display: 'flex',
                                            gap: '30px',
                                        }}>
                                        <Button
                                            type="submit"
                                            textAlign="left"
                                            data-walkthroughid={ufSaveBtn}
                                            className="h-10 min-w-[5rem] font-semibold disabled:cursor-not-allowed"
                                            isDisabled={
                                                submitting ||
                                                (typeof isSubmitDisabled ===
                                                'function'
                                                    ? isSubmitDisabled(
                                                          formProps.errors,
                                                          formProps.values,
                                                          formProps.dirty,
                                                      )
                                                    : isSubmitDisabled)
                                            }
                                            isLoading={submitting}
                                            loadingText={loadingText}
                                            sx={{ width: 142 }}>
                                            {submitText}
                                        </Button>
                                        {middleButton ? (
                                            <Button
                                                type="submit"
                                                textAlign="left"
                                                data-walkthroughid={
                                                    middleButton.ufMiddleBtn
                                                }
                                                className="h-10 min-w-[5rem] font-semibold disabled:cursor-not-allowed"
                                                isDisabled={
                                                    typeof isSubmitDisabled ===
                                                    'function'
                                                        ? isSubmitDisabled(
                                                              formProps.errors,
                                                              formProps.values,
                                                          )
                                                        : isSubmitDisabled
                                                }
                                                onClick={middleButton.onClick}
                                                sx={{ width: 142 }}>
                                                {middleButton.text}
                                            </Button>
                                        ) : null}
                                        {!noBackBtn && (
                                            <Button
                                                className="secondary h-10 min-w-[5rem] font-semibold"
                                                variant="outline"
                                                background="white"
                                                sx={{ width: 142 }}
                                                data-walkthroughid={ufBackBtn}
                                                onClick={navBack}>
                                                Back
                                            </Button>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            flex: 1,
                                        }}>
                                        {extraButton}
                                    </div>
                                </Stack>
                            </Stack>
                        </form>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default FormAccordionBase;
